import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import ReactApexChart from 'react-apexcharts';
import { StatisticsItem } from '../HistoryDetail/types';
import { StyledChartHeader, StyledChartTitle, StyledChartWrapper, StyledPageArrow } from './styles';
import { icons } from '@assets/icons';
import { comma } from 'utils/lib';
import StatisticsFilter from '@components/molecules/History/StatisticsFilter';

interface Props {
  minDate?: Date;
  maxDate?: Date;
  selectedDate: any;
  total: number;
  items: StatisticsItem[];
  onChangeDate: (newData: any) => void;
}

const HistoryDetailStatistics = ({
  minDate,
  maxDate,
  selectedDate,
  total,
  items,
  onChangeDate,
}: Props) => {
  const [page, setPage] = useState(1);

  /**
   * bar 개수 제한
   * 30개 이상인 경우 30개 까지만 표시
   */
  const graphCount = useMemo(() => {
    const { startDate, endDate } = selectedDate[0];
    return moment(endDate).diff(startDate, 'M') >= 0 ? 30 : total > 7 ? total : 7;
  }, [selectedDate, total]);

  /**
   * 데이터 나누기
   */
  const _data = useMemo(() => {
    const result: StatisticsItem[][] = [];

    const temp: StatisticsItem[] = [...items];

    while (temp.length !== 0) {
      const divide = temp.splice(0, graphCount);

      /**
       * 기존 순서라면 마지막 일자가 제일 앞으로 온다.
       * 그래프에 그려지는 데이터는 마지막일자가 제일 뒤로 가야한다.
       * 따라서 .reverse 함수로 순서를 뒤집는다.
       */
      const format = divide
        .map((item) => ({
          ...item,
          d: moment(item.d, 'YYYY-MM-DD').format('MM/DD'),
        }))
        .reverse();

      result.push(format);
    }

    return result;
  }, [graphCount, items]);

  /**
   * 페이지에 따른 데이터 포맷
   */
  const { chartData, xaxis } = useMemo(() => {
    const items = _data[page - 1];
    if (!items) {
      return {
        chartData: [],
        xaxis: [],
      };
    }

    const chartData: number[] = [];
    const xaxis: (string | null)[] = [];

    let restDateCount = 0;

    // 7일 미만인 경우 그래프 가운데 정렬을 위한 로직
    if (items.length < 7) {
      restDateCount = Math.ceil((7 - items.length) / 2);

      const tempArray = new Array(restDateCount).fill(null);
      const axisEmptyArray = new Array(restDateCount).fill('');

      chartData.push(...tempArray);
      xaxis.push(...axisEmptyArray);
    }

    for (let i = 0; i < items.length; i++) {
      const { d, c } = items[i];
      chartData.push(c);
      xaxis.push(d);
    }

    // 7주일 미만인 경우 그래프 가운데 정렬을 위한 로직
    // restDataCount 가 0이 아닌 경우 공백 추가
    if (restDateCount !== 0) {
      const tempArray = new Array(restDateCount).fill(null);
      const axisEmptyArray = new Array(restDateCount).fill('');

      chartData.push(...tempArray);
      xaxis.push(...axisEmptyArray);
    }

    return {
      chartData,
      xaxis,
    };
  }, [_data, page]);

  const pageHandler = (next: number) => {
    if (next === 0 || _data.length < next) return;
    setPage(next);
  };

  useEffect(() => {
    const itemTotal = items.length;
    if (itemTotal === 0) return;

    const newPage = Math.ceil(itemTotal / graphCount);

    if (newPage < page) {
      setPage(newPage);
    }
  }, [items, page, graphCount]);

  return (
    <div>
      <StatisticsFilter
        minDate={minDate}
        maxDate={maxDate}
        dateRangeSet={selectedDate}
        setSearchConditions={onChangeDate}
      />

      <StyledChartHeader>
        <StyledChartTitle>
          <strong>Analysis count</strong>
          <div className="tooltip">
            <img src={icons.warning_circle} alt="warning" />
            <div className="tooltip-wrapper">
              <div className="tooltip-content">
                <div>Count the number of times analysis </div>
                <div>has been completed in the period.</div>
              </div>
            </div>
          </div>
        </StyledChartTitle>
        <div className="total">
          <span>Total</span>
          <span>{comma(total)}</span>
        </div>
      </StyledChartHeader>

      <StyledChartWrapper>
        {_data.length === page || graphCount === 7 ? null : (
          <StyledPageArrow className="left" onClick={() => pageHandler(page + 1)} />
        )}
        {page === 1 ? null : (
          <StyledPageArrow className="right" onClick={() => pageHandler(page - 1)} />
        )}
        <ReactApexChart
          series={[
            {
              name: '',
              data: [...chartData],
            },
          ]}
          options={{
            plotOptions: {
              bar: {
                dataLabels: {
                  position: 'top',
                },
              },
            },
            chart: {
              toolbar: {
                tools: {
                  download: false,
                },
              },
              animations: {
                enabled: false,
              },
            },
            dataLabels: {
              offsetY: -20,
              style: {
                fontSize: '10px',
                fontWeight: '300',
                colors: ['#444'],
              },
            },
            tooltip: {
              enabled: false,
            },
            colors: ['#9b6ebc'],
            xaxis: {
              categories: [...xaxis],
              labels: {
                style: {
                  fontSize: '10px',
                },
                offsetY: -1,
              },
            },
            yaxis: {
              labels: {
                formatter(val) {
                  if (!val) return '';
                  return val.toFixed(0);
                },
              },
            },
          }}
          type="bar"
          height={325}
        />
      </StyledChartWrapper>
    </div>
  );
};

export default HistoryDetailStatistics;
